import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TrainingDataUpload from '../../features/training/components/TrainingDataUpload';
import TrainingConfiguration from '../../features/training/components/TrainingConfiguration';
import TrainingExecution from '../../features/training/components/TrainingExecution';
import TrainingResults from '../../features/training/components/TrainingResults';
import ViolinPlotsSection from '../../features/training/components/ViolinPlotsSection';
import { generateUploadId } from '@/core/services/upload/utils';
import { CSVFileEntry, TimeInformationDatabaseData, ZeitschritteData } from '../../types/common';
import SessionSelector, { SessionSelectorHandle } from '../../features/training/components/SessionSelector';
import type { SessionInfo } from '../../features/training/components/SessionSelector';
import { validateModelParameters, areRequiredFieldsFilled } from '../../utils/modelParameterUtils';
import { TrainingApiService } from '../../features/training/services/trainingApiService';
import { useTrainingProgress } from '../../features/training/hooks/useTrainingProgress';
import { socket } from '../../core/services/socket/socket';

// Debug flag - set to true to trace training state transitions
const DEBUG_TRAINING_PAGE = false;

// Import new components and hooks
import { POLLING_CONFIG } from '../../features/training/config/constants';
import { EnhancedApiResponse } from '../../features/training/types';
import { usePolling } from '../../features/training/hooks/usePolling';
import { useTrainingWorkflow } from '../../features/training/hooks/useTrainingWorkflow';
import { useSessionManagement } from '../../features/training/hooks/useSessionManagement';
import ErrorMessage from '../../shared/components/feedback/ErrorMessage';
import SuccessMessage from '../../shared/components/feedback/SuccessMessage';
import { trainingErrorPayloadFromError } from '../../features/training/utils/trainingErrorPayload';
import TrainingWorkflowButtons from '../../features/training/components/TrainingWorkflowButtons';
import ViolinPlotProgressDisplay from '../../shared/components/feedback/ViolinPlotProgressDisplay';
import ModelDownloadModal from '../../features/training/components/ModelDownloadModal';
import ConfirmDialog from '@/shared/components/modals/ConfirmDialog';
import { useTrainingData } from '../../features/training/hooks/useTrainingData';
import { useDatasetValidation } from '../../features/training/hooks/useDatasetValidation';
import { useViolinPlots } from '../../features/training/hooks/useViolinPlots';
import { useModelDownload } from '../../features/training/hooks/useModelDownload';
import RealTimeTrainingProgress from '../../features/training/components/RealTimeTrainingProgress';
import TrainingOverlay from '../../features/training/components/TrainingOverlay';
import { useTrainingPolling } from '../../features/training/hooks/useTrainingPolling';
import { useTrainingQuota } from '../../features/training/hooks/useTrainingQuota';
import { useAuth } from '../../features/auth/contexts/AuthContext';
import { useKeepAlive } from '@/core/services/keepalive';
import { TrainedModelsSidebar } from '../../features/training/components/TrainedModelsSidebar';
import { FaRobot, FaDatabase, FaArrowUpLong, FaArrowRotateLeft } from 'react-icons/fa6';
import { supabase } from '@/api/client/supabaseClient';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import { usePublishChatContext } from '../../features/chatbot/usePublishChatContext';
import type { ChatField } from '../../features/chatbot/types';

const Training = () => {
    const { t } = useTranslation('training');
    const { canProcessDataset, getQuotaError } = useTrainingQuota();
    const { refreshUserData } = useAuth();

    // State for messages
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // W11-FE10: critical errors (quota, FORBIDDEN, invalid params) stay
    // visible until acknowledged; transient errors keep the 5s auto-dismiss.
    // The persistent flag is set true only at error sites that detect a
    // persistent backend code via trainingErrorPayloadFromError; it resets
    // to false whenever errorMessage clears (close handler) so subsequent
    // transient errors auto-dismiss normally.
    const [errorPersistent, setErrorPersistent] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showChangeInputConfirm, setShowChangeInputConfirm] = useState(false);
    const [sessionName, setSessionName] = useState<string | undefined>(undefined);

    // State for tracking unsaved Zeitschritte data
    const [zeitschritteIsDirty, setZeitschritteIsDirty] = useState<boolean>(false);

    // State for tracking unsaved TimeInfo data
    const [timeInfoIsDirty, setTimeInfoIsDirty] = useState<boolean>(false);
    const {
        uploadSessionId,
        setUploadSessionId,
        databaseFilesCount,
        isLoadingFilesCount: _isLoadingFilesCount,
        fetchDatabaseFilesCount,
        loadExistingSessionData,
        checkSessionExists,
        createDatabaseSession,
        saveSessionToStorage,
        clearSessionFromStorage,
        resetAllData,
        resetDatabaseFilesCount
    } = useSessionManagement();
    
    const {
        workflowStep,
        setWorkflowStep,
        analysisStatus,
        setAnalysisStatus,
        analysisProgress,
        analysisCurrentStep,
        analysisEta,
        updateAnalysisProgress,
        resetAnalysisProgress,
        trainingStatus,
        setTrainingStatus,
        isGeneratingDatasets,
        setIsGeneratingDatasets,
        isTrainingModels,
        setIsTrainingModels,
        hasTrainedModel,
        datasetCount,
        modelConfiguration,
        setModelConfiguration,
        trainingDataSplit,
        setTrainingDataSplit,
        resetWorkflow,
        returnToUpload,
        returnToModelConfig,
        completeDatasetGeneration,
        completeModelTraining,
        resultsLoaded,
        restoreWorkflowState
    } = useTrainingWorkflow();
    
    // Training data management hook
    const {
        inputFiles,
        outputFiles,
        timeInfo,
        zeitschritte,
        isUploading,
        resetKey,
        setInputFiles,
        setOutputFiles,
        setTimeInfo,
        setZeitschritte,
        resetTrainingData: _resetTrainingData, // Reserved for future use
        incrementResetKey
    } = useTrainingData();

    // Dataset validation for generate datasets button
    const datasetValidation = useDatasetValidation(zeitschritte, inputFiles, outputFiles, timeInfo, zeitschritteIsDirty, timeInfoIsDirty);



    // Track unsaved changes state for warning users before leaving
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Track unsaved changes when files, timeInfo, or zeitschritte change
    useEffect(() => {
        // Only consider it unsaved if there are actual file entries or data
        const hasLocalOnlyFiles = [...(inputFiles || []), ...(outputFiles || [])].some(
            entry => entry.file !== null && !entry.storagePath
        );
        const hasTimeInfoChanges = Boolean(timeInfo && (
            timeInfo.jahr || timeInfo.monat || timeInfo.woche ||
            timeInfo.tag || timeInfo.feiertag
        ));
        const hasZeitschritteChanges = Boolean(zeitschritte && (
            zeitschritte.eingabe || zeitschritte.ausgabe ||
            zeitschritte.zeitschrittweite || zeitschritte.offset
        ));

        // Set unsaved if there are local-only files or uncommitted configuration changes
        // Note: With instant upload, files go to DB immediately, so this mainly tracks
        // configuration changes (timeInfo, zeitschritte) that haven't been saved yet
        const isUnsaved = hasLocalOnlyFiles ||
            (hasTimeInfoChanges && workflowStep === 'upload') ||
            (hasZeitschritteChanges && workflowStep === 'upload');
        setHasUnsavedChanges(isUnsaved);
    }, [inputFiles, outputFiles, timeInfo, zeitschritte, workflowStep]);

    // Warn user before leaving page with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = t('training.notifications.warnings.unsavedChanges');
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges, t]);

    // Calculate global duplicate bezeichnung IDs across both input and output files
    const globalDuplicateBezeichnungIds = useMemo(() => {
        const bezeichnungMap = new Map<string, string[]>();
        const allFiles = [...(inputFiles || []), ...(outputFiles || [])];
        
        // Only check entries that have files (not empty placeholder entries)
        allFiles.forEach(entry => {
            if (entry && (entry.file || entry.storagePath) && entry.bezeichnung?.trim()) {
                const normalizedBezeichnung = entry.bezeichnung.trim().toLowerCase();
                const existingIds = bezeichnungMap.get(normalizedBezeichnung) || [];
                existingIds.push(entry.id);
                bezeichnungMap.set(normalizedBezeichnung, existingIds);
            }
        });
        
        // Find all IDs that have duplicates
        const duplicateIds = new Set<string>();
        bezeichnungMap.forEach((ids) => {
            if (ids.length > 1) {
                ids.forEach(id => duplicateIds.add(id));
            }
        });
        
        return duplicateIds;
    }, [inputFiles, outputFiles]);

    // Refs
    const sessionSelectorRef = useRef<SessionSelectorHandle>(null);
    const mountedRef = useRef(true);
    const refreshUserDataRef = useRef(refreshUserData);

    // Keep ref updated when refreshUserData changes
    useEffect(() => {
        refreshUserDataRef.current = refreshUserData;
    }, [refreshUserData]);

    // Training progress hook
    const trainingProgress = useTrainingProgress(uploadSessionId);

    // Debug: Log overlay visibility factors
    useEffect(() => {
        if (DEBUG_TRAINING_PAGE && uploadSessionId) {
            // IMPORTANT: 'model_training_completed' should NOT hide overlay - post-training still running!
            const overlayIsVisible = isTrainingModels ||
                trainingStatus === 'running' ||
                (trainingProgress.currentProgress?.status !== undefined &&
                 !['training_completed', 'training_failed'].includes(trainingProgress.currentProgress?.status || ''));

            const isPreparingData = (isTrainingModels || trainingStatus === 'running') &&
                !trainingProgress.currentProgress;

            console.debug('[TrainingPage] 🖼️ Overlay visibility factors:', {
                overlayIsVisible,
                isPreparingData,
                isTrainingModels,
                trainingStatus,
                currentProgressStatus: trainingProgress.currentProgress?.status,
                currentProgressPhase: trainingProgress.currentProgress?.phase,
                currentProgressStep: trainingProgress.currentProgress?.step,
                hasCurrentProgress: !!trainingProgress.currentProgress
            });
        }
    }, [isTrainingModels, trainingStatus, trainingProgress.currentProgress?.status, trainingProgress.currentProgress?.phase, trainingProgress.currentProgress?.step, trainingProgress.currentProgress, uploadSessionId]);

    // Keepalive hook - prevents Cloud Run instance termination when tab is minimized
    useKeepAlive(uploadSessionId, isTrainingModels || isUploading || isGeneratingDatasets);

    // Violin plots hook
    const {
        loadViolinPlotsFromResults: loadViolinPlotsFromResultsHook
    } = useViolinPlots({
        onComplete: (plots, count) => completeDatasetGeneration(plots as Record<string, string>, count),
        onError: () => setErrorMessage(t('training.notifications.errors.dataNotCompatibleVisualization')),
        resultsLoaded
    });

    // Model download hook
    const {
        showModelModal,
        handleSaveModel: saveModelHook,
        closeModelModal
    } = useModelDownload((message) => setErrorMessage(message));

    // Training polling hook
    const {
        pollAnalysisStatus,
        pollTrainingStatus,
        pollDatasetGeneration
    } = useTrainingPolling(
        uploadSessionId,
        analysisStatus,
        mountedRef,
        {
            setAnalysisStatus,
            updateAnalysisProgress,
            setErrorMessage,
            setSuccessMessage,
            setTrainingStatus,
            completeModelTraining,
            setIsGeneratingDatasets,
            setWorkflowStep,
            setIsTrainingModels,
            loadViolinPlotsFromResults: loadViolinPlotsFromResultsHook
        },
        t
    );

    // Publish current training settings to the chatbot assistant context
    const chatContext = useMemo(() => {
        const fields: ChatField[] = [];
        const z = zeitschritte ?? {};
        if (z.zeitschrittweite) fields.push({ label: 'Zeitschrittweite', value: String(z.zeitschrittweite) });
        if (z.offset) fields.push({ label: 'Offset', value: String(z.offset) });
        if (z.eingabe) fields.push({ label: 'Eingabe-Auflösung', value: String(z.eingabe) });
        if (z.ausgabe) fields.push({ label: 'Ausgabe-Auflösung', value: String(z.ausgabe) });
        if (Array.isArray(inputFiles)) fields.push({ label: 'Eingangsdateien', value: String(inputFiles.length) });
        if (Array.isArray(outputFiles)) fields.push({ label: 'Ausgangsdateien', value: String(outputFiles.length) });
        // CSVFileEntry does not expose parsed rows/headers → dataProfile omitted (Part 2)
        return { fields };
    }, [zeitschritte, inputFiles, outputFiles]);

    usePublishChatContext(chatContext);

    // Calculate local n_dat from uploaded files in real-time
    // This gives immediate feedback without waiting for backend finalization
    const localNDat = useMemo(() => {
        const allFiles = [...(inputFiles || []), ...(outputFiles || [])];
        // Only count files that are actually uploaded (have storagePath or file)
        const uploadedFiles = allFiles.filter(f => f.storagePath || f.file);

        return uploadedFiles.reduce((total, file) => {
            const datenpunkte = parseInt(file.datenpunkte || '0', 10);
            return total + (isNaN(datenpunkte) ? 0 : datenpunkte);
        }, 0);
    }, [inputFiles, outputFiles]);
    
    // Cleanup on unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Handle training completion via Socket.IO events
    useEffect(() => {
        const status = trainingProgress.currentProgress?.status;
        if (!status || !mountedRef.current) return;

        if (DEBUG_TRAINING_PAGE) {
            console.debug('[TrainingPage] 🎯 Training status change detected:', {
                status,
                phase: trainingProgress.currentProgress?.phase,
                step: trainingProgress.currentProgress?.step,
                progress: trainingProgress.currentProgress?.progress_percent,
                isTrainingModels,
                trainingStatus
            });
        }

        // IMPORTANT: Only 'training_completed' (from orchestrator) means everything is done
        // 'model_training_completed' means Keras finished, but post-training phases are still running!
        // Guard: only fire completion if we're actually training (prevents re-trigger after "Train new model" button)
        if (status === 'training_completed') {
            if (!isTrainingModels && trainingStatus !== 'running') {
                // Stale completion status — we're not actively training, ignore it
                return;
            }
            if (DEBUG_TRAINING_PAGE) {
                console.debug('[TrainingPage] ✅ FINAL TRAINING COMPLETED - triggering completion flow');
            }
            setIsTrainingModels(false);
            completeModelTraining();
            updateAnalysisProgress(100, t('training.notifications.success.modelTrainingCompleted'));
        } else if (status === 'model_training_completed') {
            // Keras training done, but post-training phases (evaluation, upload, save) still running
            // DON'T set isTrainingModels to false - keep overlay visible!
            if (DEBUG_TRAINING_PAGE) {
                console.debug('[TrainingPage] 🔄 KERAS COMPLETED - waiting for post-training phases...');
            }
            // Just update progress message, but keep training active
            updateAnalysisProgress(
                trainingProgress.currentProgress?.progress_percent || 50,
                t('training.notifications.progress.postTrainingStarting', 'Post-training processing...')
            );
        } else if (status === 'training_failed') {
            if (DEBUG_TRAINING_PAGE) {
                console.debug('[TrainingPage] ❌ TRAINING FAILED');
            }
            setIsTrainingModels(false);
            setErrorMessage(t('training.notifications.errors.trainingFailed'));
        }
    }, [trainingProgress.currentProgress?.status, setIsTrainingModels, completeModelTraining, updateAnalysisProgress, setErrorMessage, t, isTrainingModels, trainingStatus, trainingProgress.currentProgress?.phase, trainingProgress.currentProgress?.step, trainingProgress.currentProgress?.progress_percent]);

    // Update analysis progress based on real-time training progress
    // Check BOTH latestMetrics (epoch updates) AND currentProgress (post-training updates)
    useEffect(() => {
        if (!isTrainingModels || !mountedRef.current) return;

        // Prioritize currentProgress (includes post-training progress 50-100%)
        // Fallback to latestMetrics for epoch-level progress
        const progressPercent = trainingProgress.currentProgress?.progress_percent
            ?? trainingProgress.latestMetrics?.progress_percent;
        const message = trainingProgress.currentProgress?.message
            ?? t('training.notifications.progress.trainingModels');

        if (progressPercent !== undefined && progressPercent > 0) {
            updateAnalysisProgress(progressPercent, message);
        }
    }, [trainingProgress.currentProgress?.progress_percent, trainingProgress.currentProgress?.message,
        trainingProgress.latestMetrics?.progress_percent, isTrainingModels, updateAnalysisProgress, t]);


    const handleLoadExistingSessionData = useCallback(async (sessionId: string) => {
        // console.debug('[handleLoadExistingSessionData] 🚀 Loading session:', sessionId);

        const sessionData = await loadExistingSessionData(sessionId);

        if (!sessionData || !mountedRef.current) {
            // console.debug('[handleLoadExistingSessionData] ❌ No session data or component unmounted');
            return;
        }


        // 1. Set basic session data (existing logic)
        if (sessionData.sessionName) {
            setSessionName(sessionData.sessionName);
        }
        setTimeInfo(sessionData.timeInfo);
        setZeitschritte(sessionData.zeitschritte);
        setInputFiles(sessionData.inputFiles);
        setOutputFiles(sessionData.outputFiles);

        // 2. NEW: Restore workflow state using the new function
        if (sessionData.trainingStatus && sessionData.resultsInfo) {
            // console.debug('[handleLoadExistingSessionData] 🔄 Restoring workflow state...');
            restoreWorkflowState(sessionData.trainingStatus, sessionData.resultsInfo);
        } else {
            // Fallback to legacy logic for backwards compatibility
            // console.debug('[handleLoadExistingSessionData] ⚠️ Using legacy status check');
            if (sessionData.analysisCompleted) {
                setAnalysisStatus('completed');
                updateAnalysisProgress(100, 'Training completed');
            }
        }

        // console.debug('[handleLoadExistingSessionData] ✅ Session restoration complete');

    }, [loadExistingSessionData, restoreWorkflowState, updateAnalysisProgress]);
    
    // Initialize session on mount
    useEffect(() => {
        const initializeSession = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionParam = urlParams.get('session');

            // Only load session if explicitly provided in URL
            // Don't auto-load from localStorage - user should choose which session to work with
            if (!sessionParam) {
                // No session in URL - stay on session picker, don't auto-load
                return;
            }

            const existingSessionId = sessionParam;

            // Keep session in URL so refresh works correctly
            // (removed: window.history.replaceState that was clearing the URL)

            if (existingSessionId) {
                const exists = await checkSessionExists(existingSessionId);
                if (exists && mountedRef.current) {
                    setUploadSessionId(existingSessionId);
                    await handleLoadExistingSessionData(existingSessionId);
                } else {
                    clearSessionFromStorage();
                    setUploadSessionId(null);
                }
            }
        };
        
        initializeSession();
    }, [checkSessionExists, clearSessionFromStorage, handleLoadExistingSessionData, setUploadSessionId]);


    // FIX-8 (API storm): removed redundant fetchDatabaseFilesCount auto-effect.
    // The bundled /session/<sid>/database response inside loadExistingSessionData
    // already populates databaseFilesCount via setDatabaseFilesCount(sessionData.n_dat).
    // For brand-new sessions (handleNewSession), no files exist yet so the null →
    // 0 fallback in the JSX renders correctly. handleUploadComplete still calls
    // fetchDatabaseFilesCount explicitly after uploads to refresh the count.

    // Socket.IO event listeners with proper cleanup
    useEffect(() => {
        if (!uploadSessionId) return;

        // console.debug('[Socket.IO] 🔌 Setting up socket for session:', uploadSessionId);

        const handleDatasetGenerationError = (data: { error?: string; message?: string }) => {
            // console.debug('[Socket.IO] ❌ dataset_generation_error:', data);
            if (!mountedRef.current) return;

            setIsGeneratingDatasets(false);
            resetAnalysisProgress(); // Use reset for error state

            if ((data as Record<string, unknown>).error_message) {
                setErrorMessage(t('training.notifications.errors.dataNotCompatible'));
            } else {
                setErrorMessage(t('training.notifications.errors.dataNotCompatibleViolin'));
            }
        };

        // Handle training_progress events for fast activation and progress tracking
        const handleTrainingProgress = (data: {
            session_id?: string;
            status?: string;
            phase?: string;
            progress_percent?: number;
            message?: string;
            epoch?: number;
            total_epochs?: number;
            loss?: number;
            val_loss?: number;
        }) => {
            if (!mountedRef.current) return;

            if (DEBUG_TRAINING_PAGE) {
                console.debug('[TrainingPage] 📡 Socket training_progress:', {
                    status: data.status,
                    phase: data.phase,
                    progress: data.progress_percent,
                    message: data.message?.substring(0, 60),
                    isTrainingModels
                });
            }

            // FAST PATH: If we receive training_execution phase OR training_starting status,
            // immediately show training overlay. This happens RIGHT AWAY when backend thread starts,
            // before data loading (which can take 5-30 seconds)
            // IMPORTANT: Skip if message indicates dataset generation (not model training)
            const messageLC = (data.message || '').toLowerCase();
            const isDatasetGeneration = messageLC.includes('dataset') || messageLC.includes('violin') || messageLC.includes('generating');

            const shouldActivate = (
                (data.phase === 'training_execution' || data.status === 'training_starting') &&
                !isTrainingModels &&
                !isDatasetGeneration  // Don't activate training overlay for dataset generation
            );

            if (shouldActivate) {
                if (DEBUG_TRAINING_PAGE) {
                    console.debug('[TrainingPage] ⚡ FAST ACTIVATION triggered - showing overlay');
                }
                setIsTrainingModels(true);
                setWorkflowStep('phase3');  // [WORKFLOW_DEBUG] Was phase5, now phase3 (model training)
                console.log('[WORKFLOW_DEBUG] Socket fast activation → phase3');
                setAnalysisStatus('completed'); // Datasets must be done if training started
                // Reset progress with force=true to allow training progress to start from 0
                // (otherwise monotonic check rejects because dataset was at 100%)
                updateAnalysisProgress(0, data.message || 'Starting training...', undefined, true);
            }

            // Update progress if training is in progress
            if ((data.phase === 'training_execution' || data.status === 'training_starting') && data.progress_percent !== undefined) {
                updateAnalysisProgress(
                    data.progress_percent,
                    data.message || `Epoch ${data.epoch || 0}/${data.total_epochs || 0}`
                );
            }
        };

        const handleDatasetStatusUpdate = async (data: { status?: string; error?: string; message?: string; progress?: number; eta_seconds?: number; step_eta_seconds?: number }) => {
            // console.debug('[Socket.IO] 📊 dataset_status_update:', {
            //     status: data.status,
            //     progress: data.progress,
            //     message: data.message?.substring(0, 50)
            // });
            if (!mountedRef.current) return;

            // FAST PATH: If we receive 'processing' status, immediately show progress tracker
            // This avoids waiting for the slower HTTP API call
            if (data.status === 'processing' && !isGeneratingDatasets) {
                // console.debug('[Socket.IO] ⚡ Fast activation: Dataset generation detected via Socket.IO');
                setIsGeneratingDatasets(true);
                setAnalysisStatus('running');
            }

            // Update progress bar if progress data is received
            // Prefer step_eta_seconds (specific to current step) over eta_seconds (overall)
            if (data.progress !== undefined) {
                const eta = data.step_eta_seconds ?? data.eta_seconds;
                updateAnalysisProgress(data.progress, data.message || '', eta);
            }

            if (data.status === 'data_validation_error') {
                setIsGeneratingDatasets(false);
                resetAnalysisProgress(); // Use reset for error state

                if (data.message) {
                    setErrorMessage(t('training.notifications.errors.general', { message: data.message }));
                }
            }

            // Handle dataset generation completion (especially after page refresh restoration)
            if (data.status === 'completed' && uploadSessionId) {
                // console.debug('[Socket.IO] ✅ Dataset generation completed, fetching results for session:', uploadSessionId);
                try {
                    // Small delay to allow database to propagate the saved plots
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    // Fetch the violin plots from the backend using getVisualizations
                    // Note: Backend returns 'plots' not 'violin_plots'
                    const response = await TrainingApiService.getVisualizations(uploadSessionId) as {
                        plots?: Record<string, { data: string; type: string }>;
                        violin_plots?: Record<string, string>;
                        n_dat?: number;
                        success?: boolean;
                        error?: string;
                    };

                    // Handle both formats: 'plots' (from /visualizations endpoint) and 'violin_plots' (legacy)
                    const plots = response.plots || response.violin_plots;
                    const plotCount = plots ? Object.keys(plots).length : 0;

                    if (mountedRef.current && plots && plotCount > 0) {
                        // Convert new format {data, type} to simple Record<string, string> for completeDatasetGeneration
                        const violinPlots: Record<string, string> = {};
                        for (const [name, plotData] of Object.entries(plots)) {
                            if (typeof plotData === 'string') {
                                violinPlots[name] = plotData;
                            } else if (plotData && typeof plotData === 'object' && 'data' in plotData) {
                                violinPlots[name] = plotData.data;
                            }
                        }

                        completeDatasetGeneration(violinPlots, response.n_dat || 0);
                    } else if (mountedRef.current) {
                        setIsGeneratingDatasets(false);
                    }
                } catch {
                    // Still mark as complete but without plots
                    if (mountedRef.current) {
                        setIsGeneratingDatasets(false);
                    }
                }
            }
        };

        const handleTrainingCompleted = async (data: { session_id?: string; success?: boolean; message?: string }) => {
            // console.debug('[Socket.IO] ✅ training_completed:', data);
            if (!mountedRef.current) return;

            // Start loading results - this keeps the overlay visible
            // until evaluation tables are successfully loaded
            // setIsLoadingResults(true);
            // //       console.debug('[TrainingProgress] 🔄 Training complete, starting results loading phase');

            // Complete the training workflow
            // completeModelTraining();

            // Refresh user data for quota tracking (single refresh after delay)
            // Using ref to avoid socket reconnect on refreshUserData change
            try {
                await new Promise(resolve => setTimeout(resolve, 500));
                await refreshUserDataRef.current();
            } catch {
                // Silently handle refresh failure
            }

            // Auto-save trained model to storage after successful training
            if (uploadSessionId && data.success !== false) {
                try {
                    await TrainingApiService.saveModel(uploadSessionId, 'trained_model');
                } catch {
                    // Silent fail - model can still be saved manually
                }
            }

            // Show success message
            if (data.success !== false) {
                setSuccessMessage(t('training.notifications.success.trainingCompleted'));
            }
        };

        // Handle socket disconnect during active generation/training
        const handleDisconnect = (reason: string) => {
            if (!mountedRef.current) return;
            console.warn('[Socket.IO] ⚠️ Disconnected during active session:', reason);

            // Only reset if we were actively generating/training
            if (isGeneratingDatasets) {
                setIsGeneratingDatasets(false);
                resetAnalysisProgress();
                setErrorMessage(t('training.notifications.errors.connectionLost'));
            }
            if (isTrainingModels) {
                setIsTrainingModels(false);
                setTrainingStatus('failed');
                resetAnalysisProgress();
                setErrorMessage(t('training.notifications.errors.connectionLost'));
            }
        };

        // Handle reconnect — re-join session room and request current status
        const handleReconnect = () => {
            if (!mountedRef.current) return;
            console.log('[Socket.IO] 🔄 Reconnected, re-joining session:', uploadSessionId);
            socket.emit('join_training_session', { session_id: uploadSessionId });
            socket.emit('request_dataset_status', { session_id: uploadSessionId });
            socket.emit('request_training_status', { session_id: uploadSessionId });
        };

        socket.emit('join_training_session', { session_id: uploadSessionId });
        socket.on('dataset_generation_error', handleDatasetGenerationError as (data: unknown) => void);
        socket.on('dataset_status_update', handleDatasetStatusUpdate);
        socket.on('training_completed', handleTrainingCompleted as (data: unknown) => void);
        socket.on('training_progress', handleTrainingProgress as (data: unknown) => void);
        socket.on('disconnect', handleDisconnect);
        socket.on('reconnect', handleReconnect);
        socket.emit('request_dataset_status', { session_id: uploadSessionId });
        socket.emit('request_training_status', { session_id: uploadSessionId });

        return () => {
            socket.off('dataset_generation_error', handleDatasetGenerationError);
            socket.off('dataset_status_update', handleDatasetStatusUpdate);
            socket.off('training_completed', handleTrainingCompleted);
            socket.off('training_progress', handleTrainingProgress);
            socket.off('disconnect', handleDisconnect);
            socket.off('reconnect', handleReconnect);
            socket.emit('leave_training_session', { session_id: uploadSessionId });
        };
    // Note: refreshUserData removed from deps - using refreshUserDataRef to prevent socket reconnects
    // Added isGeneratingDatasets, isTrainingModels, setWorkflowStep and setAnalysisStatus for fast Socket.IO activation
    }, [uploadSessionId, setIsGeneratingDatasets, isGeneratingDatasets, setAnalysisStatus, updateAnalysisProgress, resetAnalysisProgress, completeModelTraining, completeDatasetGeneration, isTrainingModels, setIsTrainingModels, setWorkflowStep, setTrainingStatus, t]);

    // Use polling hook for analysis status
    usePolling(pollAnalysisStatus, {
        enabled: analysisStatus === 'running' && !!uploadSessionId,
        interval: POLLING_CONFIG.ANALYSIS_STATUS_INTERVAL,
        maxAttempts: POLLING_CONFIG.MAX_POLLING_ATTEMPTS,
        onMaxAttemptsReached: () => {
            if (mountedRef.current) {
                setAnalysisStatus('failed');
                setErrorMessage(t('training.notifications.errors.analysisTimeout'));
            }
        }
    });

    // Use polling hook for training status
    usePolling(pollTrainingStatus, {
        enabled: trainingStatus === 'running' && !!uploadSessionId,
        interval: POLLING_CONFIG.ANALYSIS_STATUS_INTERVAL,
        maxAttempts: POLLING_CONFIG.MAX_POLLING_ATTEMPTS,
        onMaxAttemptsReached: () => {
            if (mountedRef.current) {
                setTrainingStatus('failed');
                setErrorMessage(t('training.notifications.errors.trainingTimeout'));
            }
        }
    });

    const handleInputFilesChange = useCallback((updatedFiles: CSVFileEntry[]) => {
        setInputFiles(updatedFiles);
    }, []);

    const handleOutputFilesChange = useCallback((updatedFiles: CSVFileEntry[]) => {
        setOutputFiles(updatedFiles);
    }, []);

    const handleTimeInfoChange = useCallback((data: TimeInformationDatabaseData) => {
        setTimeInfo(data);
    }, []);

    const handleZeitschritteChange = useCallback((data: ZeitschritteData) => {
        setZeitschritte(data);
    }, []);

    const handleGenerateDatasets = useCallback(async () => {
        if (!uploadSessionId) {
            setErrorMessage(t('training.notifications.errors.noActiveSessionDataset'));
            return;
        }

        // Check processing quota before generating datasets
        if (!canProcessDataset) {
            setErrorMessage(getQuotaError('processing'));
            return;
        }

        const validation = validateModelParameters(modelConfiguration);
        if (!validation.isValid) {
            setErrorMessage(t('training.notifications.errors.invalidModelParams', { errors: validation.errors.join(', ') }));
            return;
        }

        if (!areRequiredFieldsFilled(modelConfiguration)) {
            setErrorMessage(t('training.notifications.warnings.fillRequiredFields'));
            return;
        }

        setIsGeneratingDatasets(true);
        setWorkflowStep('phase1');  // [WORKFLOW_DEBUG] Was phase3, now phase1 (dataset generation)
        console.log('[WORKFLOW_DEBUG] handleGenerateDatasets → phase1');
        updateAnalysisProgress(0, t('training.notifications.progress.generatingDatasets'), undefined, true); // force=true for fresh start

        try {
            const response = await TrainingApiService.generateDatasets(
                uploadSessionId,
                modelConfiguration,
                trainingDataSplit
            ) as EnhancedApiResponse;

            // Refresh usage data after dataset generation (backend increments processing counter)
            // Single refresh after delay for Supabase replication
            await new Promise(resolve => setTimeout(resolve, 500));
            await refreshUserData();

            if (mountedRef.current && response.success) {
                // Dataset generation is synchronous and returns immediately with results
                if (response.violin_plots) {
                    // Use completeDatasetGeneration to properly set violin plots and n_dat
                    completeDatasetGeneration(
                        response.violin_plots as Record<string, string>,
                        response.n_dat || response.dataset_count || 0
                    );
                    updateAnalysisProgress(100, 'Dataset generation completed');
                    // The violin plots are already generated, no need to poll
                } else {
                    // Fallback to polling if no immediate results
                    pollDatasetGeneration(uploadSessionId);
                }
            } else if (mountedRef.current) {
                pollDatasetGeneration(uploadSessionId);
            }
        } catch (error) {
            if (mountedRef.current) {
                setIsGeneratingDatasets(false);
                const errorMessage = error instanceof Error ? error.message : t('training.notifications.errors.unknown');
                setErrorMessage(t('training.notifications.errors.datasetGenerationError', { message: errorMessage }));
            }
        }
    }, [uploadSessionId, modelConfiguration, trainingDataSplit, setIsGeneratingDatasets, setWorkflowStep, updateAnalysisProgress, refreshUserData, completeDatasetGeneration]);

    // NEW: Complete 7-phase pipeline functions (commented out - using individual phase approach)
    // const handleStartCompleteTraining = async () => {
    //     if (!uploadSessionId) {

    //         return;
    //     }
    //     // Simplified version using current workflow
    //     await handleGenerateDatasets();
    // };

    const handleTrainAndValidate = useCallback(async () => {
        if (!uploadSessionId) {
            setErrorMessage(t('training.notifications.errors.noActiveSession'));
            return;
        }

        setIsTrainingModels(true);

        try {
            const response = await TrainingApiService.trainModels(
                uploadSessionId,
                modelConfiguration,
                trainingDataSplit
            );

            if (response.success) {
                setSuccessMessage(t('training.notifications.success.trainingStarted'));
                setTrainingStatus('running');
                // Keep isTrainingModels=true - will be set to false by completeModelTraining()
            } else {
                // W11-FE10: thread persistent flag through for quota/limit codes
                const payload = trainingErrorPayloadFromError({
                    code: response.error_code,
                    error: response.error,
                });
                setErrorMessage(t('training.notifications.errors.trainingError', { message: response.error || 'Unknown error' }));
                setErrorPersistent(payload.persistent);
                // Only reset on failure
                if (mountedRef.current) {
                    setIsTrainingModels(false);
                }
            }
        } catch (error) {
            setErrorMessage(t('training.notifications.errors.backendCommunication', { message: error instanceof Error ? error.message : t('training.notifications.errors.unknown') }));
            // Only reset on error
            if (mountedRef.current) {
                setIsTrainingModels(false);
            }
        }
    }, [uploadSessionId, modelConfiguration, trainingDataSplit, setIsTrainingModels, setTrainingStatus]);

    // NEW: Polling for complete training progress (simplified approach)
    // const pollCompleteTrainingProgress = async (sessionId: string) => {
    //     // Using existing dataset and model training polling instead
    // };

    // NEW: Load comprehensive evaluation results (simplified)
    // const loadComprehensiveEvaluation = async (sessionId: string) => {
    //     // Using existing loadViolinPlotsFromResults instead
    //     await loadViolinPlotsFromResults(sessionId);
    // };

    // OLD: Keep for backward compatibility, but redirect to new workflow
    // const handleRunAnalysis = async () => {
    //     await handleStartCompleteTraining();
    // };



    const handleReset = useCallback(async () => {
        if (window.confirm(t('training.confirmations.resetAllData'))) {
            try {
                setSuccessMessage(t('training.notifications.info.deletingAllData'));
                
                await resetAllData();
                
                if (mountedRef.current) {
                    setInputFiles([]);
                    setOutputFiles([]);
                    setTimeInfo({
                        session_id: '',
                        jahr: false,
                        woche: false,
                        monat: false,
                        feiertag: false,
                        tag: false,
                        zeitzone: '',
                        category_data: {}
                    });
                    setZeitschritte({
                        eingabe: '',
                        ausgabe: '',
                        zeitschrittweite: '',
                        offset: ''
                    });
                    
                    resetWorkflow();
                    trainingProgress.resetProgress(); // Reset RealTimeTrainingProgress state
                    incrementResetKey();

                    if (sessionSelectorRef.current) {
                        await sessionSelectorRef.current.refreshSessions();
                    }
                    
                    setSuccessMessage(t('training.notifications.success.allDataDeleted'));
                }
            } catch (error) {
                setErrorMessage(t('training.notifications.errors.dataResetError', { message: error instanceof Error ? error.message : t('training.notifications.errors.unknown') }));
            }
        }
    }, [resetAllData, resetWorkflow, trainingProgress]);

    const handleSessionSelect = useCallback((sessionInfo: SessionInfo) => {
        // Reset workflow state BEFORE loading new session to prevent state bleeding
        resetWorkflow();
        trainingProgress.resetProgress();

        window.history.pushState({}, '', `/training?session=${sessionInfo.sessionId}`);
        setUploadSessionId(sessionInfo.sessionId);
        setSessionName(sessionInfo.sessionName);
        handleLoadExistingSessionData(sessionInfo.sessionId);
    }, [setUploadSessionId, handleLoadExistingSessionData, resetWorkflow, trainingProgress]);

    // Callback to refresh session info after file upload
    const handleUploadComplete = useCallback(() => {
        // Refresh session list to update file count and data points
        if (sessionSelectorRef.current) {
            sessionSelectorRef.current.refreshSessions();
        }
        // Also refresh database files count for TrainingDataSplit
        fetchDatabaseFilesCount();
    }, [fetchDatabaseFilesCount]);

    const handleNewSession = useCallback(async () => {
        window.history.pushState({}, '', '/training');

        // Reset samo lokalno stanje BEZ brisanja postojećih sesija iz baze
        if (mountedRef.current) {
            setInputFiles([]);
            setOutputFiles([]);
            setTimeInfo({
                session_id: '',
                jahr: false,
                woche: false,
                monat: false,
                feiertag: false,
                tag: false,
                zeitzone: '',
                category_data: {}
            });
            setZeitschritte({
                eingabe: '',
                ausgabe: '',
                zeitschrittweite: '',
                offset: ''
            });

            resetWorkflow();
            trainingProgress.resetProgress(); // Reset RealTimeTrainingProgress state
            incrementResetKey();
            clearSessionFromStorage(); // Samo local storage, ne bazu podataka
            resetDatabaseFilesCount(); // Reset Datenpunkte counter na 0
        }

        // Kreirati novu sesiju u bazi
        try {
            const newSessionId = generateUploadId('session');
            await createDatabaseSession(newSessionId);
            // Koristi newSessionId (string format) za konzistentnost sa SessionSelector listom
            setUploadSessionId(newSessionId);
            setSessionName(undefined);
            saveSessionToStorage(newSessionId);

            // Update URL with new session ID
            window.history.pushState({}, '', `/training?session=${newSessionId}`);

            setSuccessMessage(t('training.notifications.success.newSessionCreated'));

            // Refresh session list to show new session
            if (sessionSelectorRef.current) {
                await sessionSelectorRef.current.refreshSessions();
            }
        } catch (error) {
            setErrorMessage(t('training.notifications.errors.sessionCreationFailed', {
                message: error instanceof Error ? error.message : t('training.notifications.errors.unknown')
            }));
        }
    }, [setUploadSessionId, resetWorkflow, clearSessionFromStorage, trainingProgress, resetDatabaseFilesCount, createDatabaseSession, saveSessionToStorage, t]);

    const handleSessionDelete = useCallback(async (deletedSessionId: string) => {
        // Ako je obrisana sesija trenutno aktivna, očisti stanje
        if (uploadSessionId === deletedSessionId) {
            // Očisti URL
            window.history.pushState({}, '', '/training');

            // Očisti stanje
            setUploadSessionId(null);
            clearSessionFromStorage();
            resetDatabaseFilesCount();

            // Reset ostalih podataka
            setInputFiles([]);
            setOutputFiles([]);
            setTimeInfo({
                session_id: '',
                jahr: false,
                woche: false,
                monat: false,
                feiertag: false,
                tag: false,
                zeitzone: '',
                category_data: {}
            });
            setZeitschritte({
                eingabe: '',
                ausgabe: '',
                zeitschrittweite: '',
                offset: ''
            });
            resetWorkflow();
            trainingProgress.resetProgress();
        }
    }, [uploadSessionId, setUploadSessionId, clearSessionFromStorage, resetDatabaseFilesCount, resetWorkflow, trainingProgress]);

    const handleSaveModel = useCallback(async () => {
        await saveModelHook(uploadSessionId);
    }, [uploadSessionId, saveModelHook]);

    const updateWorkflowPhase = useCallback(async (phase: string) => {
        if (!uploadSessionId) return;
        try {
            await supabase.from('sessions').update({ workflow_phase: phase }).eq('id', uploadSessionId);
        } catch (error) {
            console.error('[TrainingPage] Failed to update workflow_phase:', error);
        }
    }, [uploadSessionId]);

    const handleReturnToModelConfig = useCallback(() => {
        trainingProgress.resetProgress(); // Clear stale 'training_completed' so useEffect doesn't re-fire completeModelTraining
        returnToModelConfig();
        updateWorkflowPhase('phase2');
    }, [returnToModelConfig, updateWorkflowPhase, trainingProgress]);

    const handleChangeInputData = useCallback(() => {
        trainingProgress.resetProgress(); // Clear stale training status
        returnToUpload();
        updateWorkflowPhase('upload');
        setShowChangeInputConfirm(false);
    }, [returnToUpload, updateWorkflowPhase, trainingProgress]);

    return (
        <div className="max-w-[1600px] mx-auto">
            {/* Top row: SessionSelector + TrainedModelsSidebar */}
            <div className="flex gap-4 sm:gap-6 mb-4 sm:mb-6 h-[400px]">
                <div className="w-1/2 h-full">
                    <SessionSelector
                        ref={sessionSelectorRef}
                        currentSessionId={uploadSessionId || undefined}
                        onSessionSelect={handleSessionSelect}
                        onNewSession={handleNewSession}
                        onReset={handleReset}
                        onSessionDelete={handleSessionDelete}
                    />
                </div>

                <div className="w-1/2 h-full">
                    {uploadSessionId ? (
                        <TrainedModelsSidebar
                            sessionId={uploadSessionId}
                        />
                    ) : (
                        <div className="rounded-xl border border-border/80 bg-card shadow-sm h-full flex items-center justify-center overflow-visible">
                            <div className="text-center px-6">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
                                    <FaRobot className="text-muted-foreground text-lg" />
                                </div>
                                <p className="text-xs sm:text-sm text-muted-foreground">{t('training.sidebar.selectSessionFirst')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Main content area */}
            {uploadSessionId ? (
                <main
                    role="main"
                    aria-labelledby="training-page-title"
                    className="relative rounded-xl border border-border/80 bg-card shadow-sm overflow-visible p-5 sm:p-8"
                >
                    <h1
                        id="training-page-title"
                        className="text-lg sm:text-xl font-semibold text-foreground tracking-tight mb-5 sm:mb-6 flex items-center"
                    >
                        {t('training.page.title')}
                        <InfoTooltip text={t('tooltips.trainingSection')} width="w-96" />
                    </h1>

                    {errorMessage && (
                        <ErrorMessage
                            message={errorMessage}
                            duration={errorPersistent ? 0 : 5000}
                            onClose={() => {
                                setErrorMessage(null);
                                setErrorPersistent(false);
                            }}
                        />
                    )}
                    {successMessage && (
                        <SuccessMessage
                            message={successMessage}
                            onClose={() => setSuccessMessage(null)}
                        />
                    )}

                    <div className={`transition-opacity duration-200 ${
                        (workflowStep === 'phase2' || workflowStep === 'phase3' || workflowStep === 'phase4' || workflowStep === 'completed')
                            ? 'opacity-50 pointer-events-none select-none'
                            : ''
                    }`}>
                        <TrainingDataUpload
                            resetKey={resetKey}
                            zeitschritte={zeitschritte}
                            uploadSessionId={uploadSessionId}
                            onZeitschritteChange={handleZeitschritteChange}
                            onZeitschritteDirtyChange={setZeitschritteIsDirty}
                            onInputFilesChange={handleInputFilesChange}
                            onTimeInfoChange={handleTimeInfoChange}
                            onTimeInfoDirtyChange={setTimeInfoIsDirty}
                            onOutputFilesChange={handleOutputFilesChange}
                            globalDuplicateBezeichnungIds={globalDuplicateBezeichnungIds}
                            onUploadComplete={handleUploadComplete}
                            inputFileCount={inputFiles?.length ?? 0}
                        />
                    </div>

                    <div className="space-y-4 sm:space-y-6">
                        <TrainingWorkflowButtons
                            workflowStep={workflowStep}
                            uploadSessionId={uploadSessionId}
                            isUploading={isUploading}
                            isGeneratingDatasets={isGeneratingDatasets}
                            isDataValid={datasetValidation.isValid}
                            validationErrors={datasetValidation.errors}
                            zeitschritteStatus={datasetValidation.zeitschritteStatus}
                            inputFilesStatus={datasetValidation.inputFilesStatus}
                            outputFilesStatus={datasetValidation.outputFilesStatus}
                            duplicateBezeichnungStatus={datasetValidation.duplicateBezeichnungStatus}
                            timeInfoStatus={datasetValidation.timeInfoStatus}
                            onGenerateDatasets={handleGenerateDatasets}
                        />

                        {uploadSessionId && resultsLoaded && (workflowStep === 'phase2' || workflowStep === 'phase3' || workflowStep === 'phase4' || workflowStep === 'completed') && (
                            <>
                                <ViolinPlotsSection uploadSessionId={uploadSessionId} />
                                {workflowStep === 'phase2' && (
                                    <div className="flex justify-center">
                                        <button
                                            type="button"
                                            onClick={returnToUpload}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm font-medium text-muted-foreground bg-card border border-border/80 rounded-lg hover:bg-muted hover:text-foreground active:scale-[0.98] transition-all duration-150 shadow-sm"
                                        >
                                            <FaArrowUpLong className="text-[10px]" />
                                            {t('training.buttons.backToUpload')}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {uploadSessionId && (workflowStep === 'phase2' || workflowStep === 'phase3' || workflowStep === 'phase4' || workflowStep === 'completed') && (
                            <>
                                <TrainingConfiguration
                                    datasetCount={datasetCount ?? 0}
                                    databaseFilesCount={localNDat > 0 ? localNDat : (databaseFilesCount ?? 0)}
                                    inputFilesLength={inputFiles.length}
                                    outputFilesLength={outputFiles.length}
                                    modelConfiguration={modelConfiguration}
                                    isUploading={isUploading}
                                    isGeneratingDatasets={isGeneratingDatasets}
                                    isTrainingModels={isTrainingModels}
                                    onModelConfigChange={setModelConfiguration}
                                    onTrainingSplitChange={setTrainingDataSplit}
                                    sessionId={uploadSessionId || undefined}
                                />

                                <TrainingExecution
                                    isTrainingModels={isTrainingModels}
                                    isGeneratingDatasets={isGeneratingDatasets}
                                    isUploading={isUploading}
                                    trainingStatus={trainingStatus}
                                    uploadSessionId={uploadSessionId}
                                    hasTrainedModel={hasTrainedModel}
                                    onTrainAndValidate={handleTrainAndValidate}
                                    onSaveModel={handleSaveModel}
                                />
                            </>
                        )}

                        {uploadSessionId && hasTrainedModel && (
                            <TrainingResults
                                uploadSessionId={uploadSessionId}
                                sessionName={sessionName}
                            />
                        )}

                        {uploadSessionId && workflowStep === 'phase4' && hasTrainedModel && (
                            <div className="flex items-center justify-center gap-3 pt-2 pb-4">
                                <button
                                    type="button"
                                    onClick={handleReturnToModelConfig}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary bg-card border border-primary/20 rounded-lg hover:bg-accent hover:text-primary active:scale-[0.98] transition-all duration-150 shadow-sm"
                                >
                                    <FaArrowRotateLeft className="text-xs" />
                                    {t('training.buttons.newModelTraining')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowChangeInputConfirm(true)}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-600 bg-card border border-amber-200/80 rounded-lg hover:bg-amber-50 hover:text-amber-700 active:scale-[0.98] transition-all duration-150 shadow-sm"
                                >
                                    <FaArrowUpLong className="text-xs" />
                                    {t('training.buttons.changeInputData')}
                                </button>
                            </div>
                        )}

                        {/* Return-to-upload confirmation (FIX-7: shared ConfirmDialog migration) */}
                        <ConfirmDialog
                            isOpen={showChangeInputConfirm}
                            title={t('training.confirmReturnToUpload.title')}
                            message={t('training.confirmReturnToUpload.message')}
                            suggestion={t('training.confirmReturnToUpload.suggestion')}
                            confirmLabel={t('training.confirmReturnToUpload.confirm')}
                            cancelLabel={t('training.confirmReturnToUpload.cancel')}
                            destructive={true}
                            onConfirm={handleChangeInputData}
                            onCancel={() => setShowChangeInputConfirm(false)}
                        />


                        <TrainingOverlay
                            isVisible={
                                isTrainingModels ||
                                trainingStatus === 'running' ||
                                (trainingProgress.currentProgress?.status !== undefined &&
                                 !['training_completed', 'training_failed', 'completed', 'failed', 'not_found', 'unknown', 'idle'].includes(trainingProgress.currentProgress?.status || ''))
                            }
                            isPreparingData={
                                (isTrainingModels || trainingStatus === 'running') &&
                                !trainingProgress.currentProgress
                            }
                            connectionError={trainingProgress.connectionError}
                        >
                            <RealTimeTrainingProgress
                                isConnected={trainingProgress.isConnected && !!uploadSessionId}
                                currentProgress={trainingProgress.currentProgress}
                                progressPercentage={trainingProgress.progressPercentage}
                                currentPhase={trainingProgress.currentPhase}
                                latestMetrics={trainingProgress.latestMetrics}
                                lastError={trainingProgress.lastError}
                                connectionError={trainingProgress.connectionError}
                                onClearError={trainingProgress.clearError}
                                batchProgress={trainingProgress.batchProgress}
                            />
                        </TrainingOverlay>

                        <ViolinPlotProgressDisplay
                            isGeneratingDatasets={isGeneratingDatasets}
                            analysisProgress={analysisProgress}
                            analysisCurrentStep={analysisCurrentStep}
                            eta={analysisEta}
                        />
                    </div>

                    <ModelDownloadModal
                        isOpen={showModelModal}
                        onClose={closeModelModal}
                        sessionId={uploadSessionId || ''}
                        onModelSaved={() => {
                            closeModelModal();
                            setSuccessMessage(t('training.notifications.success.modelSaved'));
                        }}
                    />
                </main>
            ) : (
                <div className="rounded-xl border border-border/80 bg-card shadow-sm overflow-visible flex items-center justify-center min-h-[400px]">
                    <div className="text-center px-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
                            <FaDatabase className="text-muted-foreground text-lg" />
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground">{t('training.selectOrCreateSession')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Training;    